import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { PrizeCoverImage } from './components/PrizeCoverImage';
import RankingSelector from './components/RankingSelector';
import SideContentInfo from './components/SideContentInfo';

import { Loader, Tabs } from '@/components';
import { Alert } from '@/components/Alert';
import {
  Layout,
  ParticipantsAvatarStack,
  ParticipantsModal,
} from '@/components/GenericsTournaments';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTapTourney from '@/hooks/useTapTourney';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { viewTapTourneyData } from '@/services/tda';
import { EnumDevice } from '@/types';
import { SelectedScreen } from '@/types/editProfile';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

const ViewTapTourney = () => {
  const { tapTourneyData, updateTapProperties } = useTapTourney();
  const { isDesktop, isMobile } = useIsDesktop();
  const [touneyTitle, setTouneyTitle] = useState('');
  // Nuevo state para guardar la data lateral fija
  const [fixedLateralData, setFixedLateralData] = useState<any>(undefined);
  const [showModalParticipants, setShowModalParticipants] = useState(false);

  const { userInitialData } = useAuth();

  // este se va a usar para las distintas tablas
  const { tab: tabTable, handleTabChanges: handleTabTable } =
    useHandleChangesTabs();

  const navigate = useNavigate();
  const toast = useToast();

  const canFetchData = !!(
    tapTourneyData &&
    tapTourneyData.tourneyId &&
    tapTourneyData.tourneyDate
  );

  const { data: dataInit, isLoading } = useQuery(
    ['initDataTAP', tapTourneyData?.tourneyId, tapTourneyData?.tourneyDate?.id],
    async () => {
      if (
        tapTourneyData &&
        tapTourneyData.tourneyId &&
        tapTourneyData.tourneyDate?.id
      ) {
        return await viewTapTourneyData(
          tapTourneyData.tourneyId,
          tapTourneyData.tourneyDate.id
        );
      }
      throw new Error('Datos necesarios no disponibles');
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data?.nombre !== touneyTitle) {
          setTouneyTitle(data?.nombre ?? '');
        }
        // Setear fixedLateralData solo la primera vez
        if (!fixedLateralData) {
          setFixedLateralData(data);
        }
        // Setear el orden de la primera fecha
        updateTapProperties({
          tourneyDate: {
            ...tapTourneyData?.tourneyDate,
            id: tapTourneyData?.tourneyDate?.id ?? 0,
            ordenFC: data.ordenFC,
          },
        });
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.showError(err);
      },
      enabled: canFetchData,
    }
  );

  const tableTabs: DefaultTabsProps['tabs'] = [
    { name: 'Fecha', disabled: false },
    {
      name: 'Torneo General',
      disabled: false,
    },
  ];

  const onHandleTableTabsChanges = (tab: TabType) => {
    handleTabTable(tab);
  };

  const mobileLeftContent = () => {
    if (isMobile) {
      return {
        backAction: () => {
          navigate(ROUTES.friendsTournamentPrizes, {
            state: { showtourneys: true },
          });
        },
      };
    }
    return {
      showMenuAction: true,
      items: true,
    };
  };

  const countParticipant = useMemo(() => {
    if (!dataInit) {
      return 0;
    } else {
      if (dataInit && dataInit?.top_fecha) return dataInit?.top_fecha.length;
    }
    return 0;
  }, [dataInit]);

  const getParticipantPhoto = useCallback(
    (item: any) =>
      item.id === userInitialData?.idUsuarioDT
        ? userInitialData?.pathImagenPerfil
        : item.fotoPerfil,
    [userInitialData?.idUsuarioDT, userInitialData?.pathImagenPerfil]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {<Loader isVisible={isLoading} timeout={true} />}
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.torneodeamigospremios}
        backButton={isDesktop}
        backButtonAction={() =>
          navigate(ROUTES.friendsTournamentPrizes, {
            state: { showtourneys: true },
          })
        }
        className="px-4"
        coverTop={() => (
          <>
            <PrizeCoverImage
              prize={tapTourneyData?.tourneyPrize}
              touneyData={dataInit}
            />
            {isMobile && !tapTourneyData?.llenoFormBancario && (
              <Alert
                className="bg-gdt-blue w-full lg:mt-5 "
                message={
                  <div className="flex flex-wrap">
                    <span className="me-1">
                      Cargá los datos de tu CBU por si resultás ser el ganador.{' '}
                    </span>
                    <strong
                      className="cursor-pointer flex-auto whitespace-nowrap"
                      onClick={() =>
                        navigate(ROUTES.editAccount.myCBU, {
                          state: { selectedScreen: 'MyCBU' } as SelectedScreen,
                        })
                      }
                    >
                      Completar
                    </strong>
                  </div>
                }
                variant={EnumDevice.Mobile}
              />
            )}
          </>
        )}
        main={() => (
          <div>
            {isDesktop && (
              <div className="flex flex-col items-start gap-2 mb-6">
                <div className="h-[100px]">
                  <p className="text-[24px] font-bold mt-4">{touneyTitle}</p>
                  <ParticipantsAvatarStack
                    participants={(dataInit?.top_fecha || []).map(
                      (item, index) => ({
                        key: index,
                        alt: item.dt,
                        dtName: item.dt,
                        photoUrl: getParticipantPhoto(item),
                      })
                    )}
                    total={countParticipant}
                    onClick={() => setShowModalParticipants(true)}
                  />
                </div>
              </div>
            )}
            <RankingSelector
              activeTab={tabTable}
              fixedLateralData={fixedLateralData}
              tableTabs={tableTabs}
              tourneyData={dataInit}
              onHandleTableTabsChanges={onHandleTableTabsChanges}
            />
          </div>
        )}
        mobile={{
          leftContent: mobileLeftContent(),
          centerContent: {
            title: dataInit?.nombre,
          },
        }}
        sidebar={() => <SideContentInfo tourneyData={fixedLateralData} />}
        tabs={() => {
          if (isMobile) {
            return (
              <Tabs
                activeTab={tabTable}
                tabs={tableTabs}
                onClick={onHandleTableTabsChanges}
              />
            );
          }
        }}
      />
      {/* //MODAL PARTICIPANTES */}
      <ParticipantsModal
        isOpen={showModalParticipants}
        participants={(dataInit?.top_fecha || []).map((item, index) => ({
          key: index + item.dt,
          alt: item.dt,
          dtName: item.dt,
          photoUrl: getParticipantPhoto(item),
          title: item.dt,
          subtitle: item.equipo,
        }))}
        total={countParticipant}
        onClose={() => setShowModalParticipants(false)}
      />
    </>
  );
};

export default ViewTapTourney;
